import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { DashboardKpiCard } from "@/components/dashboard/DashboardKpiCard";
import { ModuleConsoleFooter } from "@/components/composition/ModuleConsoleFooter";
import { StockLevelsChart } from "@/components/dashboard/StockLevelsChart";
import { ChartSkeleton } from "@/components/skeleton/ChartSkeleton";
import { PageHeader } from "@/components/PageHeader";
import { TableSkeleton } from "@/components/skeleton/TableSkeleton";
import { TablePagination } from "@/components/TablePagination";
import { useClientPagination } from "@/hooks/useClientPagination";
import { t } from "@/core/composition/lexicon";
import { pharmacyInventoryApi } from "@/core/inventory/inventory-api";
import { IscApiError } from "@/core/inventory/isc-client";
import {
  depotSupplyRoutes,
  STOCK_DEPOTS,
  type StockDepotId,
} from "@/core/inventory/stock-depots";
import {
  computeLowStockAlerts,
  type LowStockAlert,
} from "@/core/pharmacy/dashboard-analytics";
import type { StockProduct } from "@/core/pharmacy/types";
import { routes } from "@/router/all_routes";

interface StockRow {
  product: StockProduct;
  onHand: number;
  reserved: number;
  available: number;
  threshold: number;
  isLow: boolean;
}

function isApiOfflineError(err: unknown): boolean {
  return err instanceof IscApiError && err.status === 0;
}

function IconBox() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden>
      <path
        d="M12 2 3 7v10l9 5 9-5V7l-9-5zM3 7l9 5 9-5M12 22V12"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function IconAlert() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden>
      <path
        d="M12 9v4M12 17h.01M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      />
    </svg>
  );
}

export function SupplyStockLevelsPage({ depot }: { depot: StockDepotId }) {
  const depotConfig = STOCK_DEPOTS[depot];
  const depotRoutes = depotSupplyRoutes(depot);
  const [rows, setRows] = useState<StockRow[]>([]);
  const [alerts, setAlerts] = useState<LowStockAlert[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [apiOffline, setApiOffline] = useState(false);
  const [search, setSearch] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setApiOffline(false);
    setError(null);
    try {
      const storeId = await pharmacyInventoryApi.getDepotStoreId(depot);
      const [products, balances, globalMin] = await Promise.all([
        pharmacyInventoryApi.listProductsForDepot(depot),
        storeId ? pharmacyInventoryApi.listStockBalancesForStore(storeId) : [],
        pharmacyInventoryApi.getGlobalMinThreshold(),
      ]);

      const balanceMap = new Map(
        balances.map((b) => [
          b.productId,
          { onHand: b.quantityOnHand, reserved: b.quantityReserved, available: b.quantityAvailable },
        ]),
      );

      const resolveThreshold = (articleMin: number) =>
        articleMin > 0 ? articleMin : globalMin > 0 ? globalMin : 0;

      const nextRows: StockRow[] = products
        .filter((p) => p.status === "ACTIVE")
        .map((product) => {
          const bal = balanceMap.get(product.id) ?? { onHand: 0, reserved: 0, available: 0 };
          const threshold = resolveThreshold(product.minThreshold);
          return {
            product,
            onHand: bal.onHand,
            reserved: bal.reserved,
            available: bal.available,
            threshold,
            isLow: threshold > 0 && bal.onHand < threshold,
          };
        })
        .sort((a, b) => {
          if (a.isLow !== b.isLow) return a.isLow ? -1 : 1;
          return a.onHand - b.onHand;
        });

      setRows(nextRows);
      setAlerts(computeLowStockAlerts(products, balances, globalMin));
    } catch (err) {
      setApiOffline(isApiOfflineError(err));
      setError(
        isApiOfflineError(err)
          ? "API stock inaccessible."
          : err instanceof Error
            ? err.message
            : "Chargement impossible.",
      );
      setRows([]);
      setAlerts([]);
    } finally {
      setLoading(false);
    }
  }, [depot]);

  useEffect(() => {
    void load();
  }, [load]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter(
      ({ product }) =>
        product.label.toLowerCase().includes(q) ||
        product.sku.toLowerCase().includes(q) ||
        product.code.toLowerCase().includes(q),
    );
  }, [rows, search]);

  const pagination = useClientPagination(filtered, [search], 25);
  const { pagedItems } = pagination;

  const stats = useMemo(() => {
    const totalUnits = rows.reduce((s, r) => s + r.onHand, 0);
    const totalReserved = rows.reduce((s, r) => s + r.reserved, 0);
    return {
      skuCount: rows.length,
      totalUnits,
      totalReserved,
      lowCount: alerts.length,
    };
  }, [rows, alerts.length]);

  const chartData = useMemo(
    () =>
      alerts.slice(0, 6).map((a) => ({
        name: a.label,
        quantity: a.quantity,
        threshold: a.minThreshold,
      })),
    [alerts],
  );

  return (
    <div className="ph-dashboard">
      <PageHeader
        title={`${depotConfig.label} — ${t("supply.levels")}`}
        description={`Dépôt ${depotConfig.storeCode} — alertes et soldes isolés.`}
        actions={
          <div className="d-flex gap-2 flex-wrap ph-page-header-actions">
            <Link to={depotRoutes.articles} className="btn btn-outline-secondary btn-sm">
              {t("supply.articles")}
            </Link>
            <Link to={depotRoutes.receipts} className="btn btn-outline-secondary btn-sm">
              {t("supply.receipts")}
            </Link>
            <Link to={routes.supplyAdminConsole} className="btn btn-outline-secondary btn-sm">
              {t("action.advancedModule")}
            </Link>
            <button
              type="button"
              className="btn btn-outline-secondary btn-sm"
              onClick={() => void load()}
              disabled={loading}
            >
              Actualiser
            </button>
          </div>
        }
      />

      {error && (
        <div className="alert alert-warning py-2 mb-3">
          {error}
          {apiOffline && (
            <span className="ms-1 small">
              Lancez inventory API (:3020) et web (:3021).
            </span>
          )}
        </div>
      )}

      <div className="row g-3 mb-4">
        <div className="col-6 col-xl-3">
          <DashboardKpiCard
            label={t("inventory.articles")}
            value={String(stats.skuCount)}
            hint="Référencés actifs"
            icon={<IconBox />}
            accent="teal"
          />
        </div>
        <div className="col-6 col-xl-3">
          <DashboardKpiCard
            label={t("stock.onHand")}
            value={String(stats.totalUnits)}
            hint={`${t("stock.reserved")} : ${stats.totalReserved}`}
            icon={<IconBox />}
            accent="emerald"
          />
        </div>
        <div className="col-6 col-xl-3">
          <DashboardKpiCard
            label={t("stock.lowAlert")}
            value={String(stats.lowCount)}
            hint={t("stock.threshold")}
            icon={<IconAlert />}
            accent="amber"
          />
        </div>
        <div className="col-6 col-xl-3">
          <DashboardKpiCard
            label={t("stock.available")}
            value={String(Math.max(0, stats.totalUnits - stats.totalReserved))}
            hint="Après réservations facture"
            icon={<IconBox />}
            accent="violet"
          />
        </div>
      </div>

      <div className="row g-3 mb-4">
        <div className="col-lg-5">
          <div className="ph-dash-card h-100">
            <div className="ph-dash-card-head">
              <div>
                <h3 className="ph-dash-card-title">{t("stock.lowAlert")}</h3>
                <p className="ph-dash-card-sub">Articles sous {t("stock.threshold").toLowerCase()}</p>
              </div>
            </div>
            {loading ? <ChartSkeleton height={220} /> : <StockLevelsChart data={chartData} />}
          </div>
        </div>
        <div className="col-lg-7">
          <div className="ph-dash-card h-100">
            <div className="ph-dash-card-head mb-3 ph-stock-list-head">
              <div>
                <h3 className="ph-dash-card-title">Détail par {t("inventory.article").toLowerCase()}</h3>
                <p className="ph-dash-card-sub">
                  {t("stock.onHand")} · {t("stock.reserved")} · {t("stock.available")}
                </p>
              </div>
              <input
                type="search"
                className="form-control form-control-sm w-100 w-md-auto"
                style={{ maxWidth: "14rem" }}
                placeholder="Rechercher…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>

            {loading ? (
              <TableSkeleton rows={10} columns={6} />
            ) : filtered.length === 0 ? (
              <p className="text-muted small mb-0">Aucun article.</p>
            ) : (
              <div className="table-responsive ph-diag-table">
                <table className="table table-sm ph-dash-table align-middle mb-0">
                  <thead>
                    <tr>
                      <th>SKU</th>
                      <th>Libellé</th>
                      <th className="text-end">{t("stock.onHand")}</th>
                      <th className="text-end">{t("stock.reserved")}</th>
                      <th className="text-end">{t("stock.available")}</th>
                      <th className="text-end">{t("stock.threshold")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pagedItems.map((row) => (
                      <tr
                        key={row.product.id}
                        className={`ph-diag-row${row.isLow ? " ph-stock-row--low" : ""}`}
                      >
                        <td data-label="SKU">
                          <code className="small">{row.product.sku}</code>
                        </td>
                        <td data-label="Libellé" className="fw-medium">
                          {row.product.label}
                        </td>
                        <td data-label={t("stock.onHand")} className="text-end">
                          {row.onHand}
                        </td>
                        <td data-label={t("stock.reserved")} className="text-end text-muted">
                          {row.reserved}
                        </td>
                        <td data-label={t("stock.available")} className="text-end">
                          {row.available}
                        </td>
                        <td data-label={t("stock.threshold")} className="text-end">
                          {row.threshold > 0 ? (
                            row.isLow ? (
                              <span className="badge bg-warning text-dark">{row.threshold}</span>
                            ) : (
                              row.threshold
                            )
                          ) : (
                            "—"
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <TablePagination
                  page={pagination.page}
                  pageSize={pagination.pageSize}
                  totalItems={pagination.totalItems}
                  onPageChange={pagination.setPage}
                  onPageSizeChange={pagination.setPageSize}
                />
              </div>
            )}
          </div>
        </div>
      </div>

      <ModuleConsoleFooter consoleRoute={routes.supplyAdminConsole} moduleLabel="ITaaS Inventory" />
    </div>
  );
}

export function SupplyMedicinesLevelsPage() {
  return <SupplyStockLevelsPage depot="pharmacy" />;
}

export function SupplyConsumablesLevelsPage() {
  return <SupplyStockLevelsPage depot="consumables" />;
}
